import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import {
  Card,
  Row,
  Col,
  Button,
  Icon,
  Spin,
} from 'antd';
import { Document, Page, pdfjs } from 'react-pdf';
// import workerSrc from 'pdfjs-dist/build/pdf.worker.min.js?url';
import { SpText, SpH5 } from 'components/DesignKit';

// pdfjs.GlobalWorkerOptions.workerSrc = require.resolve('pdfjs-dist/build/pdf.worker.min.js');
// pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const PdfCard = styled(Card)`
  position: fixed;
  top: 15%;
  right: 0;
  transform: ${({ show }) => (
    show
      ? 'translateX(0) translateY(0%)'
      : 'translateX(100%) translateY(0%)'
  )};
  transition: transform 0.3s ease;
  z-index: 1000;
  width: 40%;
  height: 70%;
  min-height: 500px;
  min-width: 300px;
  border: 1px solid #d0d0d0;
  padding-bottom: 10px;
  padding-right: 10px;
  border-radius: 0 7px 7px 7px;
  .ant-card-body {
    height: 100%;
  }
  .ant-spin-container {
    height: 95%;
    width: 100%;
  }
  .ant-spin-nested-loading {
    width: 100%;
    height: 100%;
  }
`;

const ToggleButton = styled(Button)`
  position: absolute;
  top: 0%;
  left: 0;
  transform: translate(-100%, 0px);
  z-index: 1001;
  width: 50px;
  height: 50px;
  border-radius: 7px 0 0 7px;
  background-color: #12284a !important;
  color: white !important;
`;

const HeaderRow = styled(Row)`
  height: 50px;
  width: 95%;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: center;
  align-content: center;
`;

const PdfContainer = styled.div`
  width: 95%;
  height: 85%;
  border: solid 1px #d0d0d0;
  border-radius: 7px;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  overflow: auto;
`;

const NavButton = styled(Button)`
  border-radius: 4px;
  background-color: ${({ disabled }) => (disabled ? '#c0c0c0' : '#12284a')} !important;
  color: #fff !important;
  & > span {
    color: #fff !important;
  }
`;

type Props = {
  pdfData: Object,
  pdfName: String,
  showPdfCard: Boolean,
  setShowPdfCard: Function,
  loading: Boolean,
};

const ViewInvoice = (props: Props) => {
  const {
    pdfData,
    pdfName,
    showPdfCard,
    setShowPdfCard,
    loading,
  } = props;

  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (showPdfCard && containerRef.current) {
      setContainerWidth(containerRef.current.offsetWidth);
    }
  }, [showPdfCard, containerRef.current]);

  const handlePrevious = () => {
    if (currentPage > 1) setCurrentPage((state) => state - 1);
  };

  const handleNext = () => {
    if (currentPage < totalPages) setCurrentPage((state) => state + 1);
  };

  const onSuccessfulLoad = ({ numPages }) => {
    if (numPages >= 1) {
      setTotalPages(numPages);
    }
  };

  return (
    <PdfCard show={showPdfCard} bodyStyle={{ padding: 0 }}>
      <ToggleButton
        type="primary"
        onClick={() => setShowPdfCard((state) => !state)}
      >
        {showPdfCard ? (
          <Icon type="caret-right" style={{ fontSize: 24, marginRight: 8 }} />
        ) : (
          <Icon type="caret-left" style={{ fontSize: 24 }} />
        )}
      </ToggleButton>
      <Spin
        spinning={loading}
        tip="Please wait..."
        size="large"
        delay={300} // delay in ms to avoid flicker
      >
        <HeaderRow gutter={16} className="mb-2 mr-4 ml-4">
          <Col
            span={12}
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'start',
              alignItems: 'center',
              // transform: 'translate(-50px, 0)',
            }}
          >
            <SpH5>PDF Preview</SpH5>
          </Col>
          <Col
            span={12}
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'end',
              alignItems: 'center',
            }}
          >
            <SpH5 color="#3e8bff">{pdfName}</SpH5>
          </Col>
        </HeaderRow>

        <PdfContainer className="mb-2 mr-4 ml-4" ref={containerRef}>
          <Document file={{ data: pdfData }} onLoadSuccess={onSuccessfulLoad}>
            <Page pageNumber={currentPage} width={containerWidth - 32} />
          </Document>
        </PdfContainer>

        <Row
          gutter={16}
          className="mb-2 mr-4 ml-4 mt-2"
          style={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'nowrap',
            justifyContent: 'center',
            alignContent: 'center',
            alignItems: 'center',
            width: '97%',
          }}
        >
          <Col span={4}>
            <NavButton
              type="primary"
              onClick={handlePrevious}
              disabled={currentPage <= 1}
            >
              <SpText>Previous</SpText>
            </NavButton>
          </Col>
          <Col span={16} style={{ display: 'flex', justifyContent: 'center' }}>
            <SpText>
              Page {currentPage} out of {totalPages}
            </SpText>
          </Col>
          <Col
            span={4}
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <NavButton
              type="primary"
              onClick={handleNext}
              disabled={currentPage >= totalPages}
            >
              <SpText>Next</SpText>
            </NavButton>
          </Col>
        </Row>
      </Spin>
    </PdfCard>
  );
};

export default ViewInvoice;
